import { createFileRoute, Link } from "@tanstack/react-router";
import { useTenant } from "../lib/tenant-context";

export const Route = createFileRoute("/docs_/getting-started")({
  component: GettingStartedPage,
});

function GettingStartedPage() {
  const { currentTenant } = useTenant();
  
  // Get tenant-specific examples
  const getExampleProduct = () => {
    if (currentTenant.id === 'arcteryx') {
      return { title: "Arc'teryx Beta AR Jacket", brand: "Arc'teryx", category: "Shell Jackets", condition: "A", original_price: 625 };
    }
    if (currentTenant.id === 'nespresso') {
      return { title: "Nespresso Vertuo Pods - 50 Pack", brand: "Nespresso", category: "Coffee Pods", condition: "New", original_price: 45 };
    }
    return { title: "Gucci Marmont Bag", brand: "Gucci", category: "Handbags", condition: "B", original_price: 2300 };
  };
  
  const getExampleResponse = () => {
    if (currentTenant.id === 'arcteryx') {
      return { success: true, estimate: { low: 280, mid: 340, high: 400, currency: "USD", confidence: 0.92 }, ai_insights: { demand: "very high", trend: "increasing", recommendation: "Premium outdoor brand with strong resale value" } };
    }
    return { success: true, estimate: { low: 1380, mid: 1610, high: 1840, currency: "USD", confidence: 0.92 }, ai_insights: { demand: "high", trend: "stable", recommendation: "Price competitively for quick sale" } };
  };
  
  return (
    <div className="max-w-4xl mx-auto bg-[#F5F5F5] -m-6 p-8 min-h-screen">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center gap-2 text-sm" style={{ color: "#A9A9A9" }}>
            <li>
              <Link to="/docs" className="hover:text-[#171717] transition-colors">
                Documentation
              </Link>
            </li>
            <li>/</li>
            <li style={{ color: "#171717" }}>Getting Started</li>
          </ol>
        </nav>

        <h1 className="text-4xl mb-6" style={{ fontWeight: 300, color: "#171717", lineHeight: 1.1 }}>
          Getting Started with Product Intelligence Cloud
        </h1>

        <div className="space-y-8">
          <p className="text-lg mb-8" style={{ color: "#171717", fontWeight: 300, lineHeight: 1.4 }}>
            Welcome to the Product Intelligence Cloud! This guide will help you
            get up and running in minutes. Follow these steps to start enriching
            your product data with AI-powered intelligence.
          </p>

          {/* Step 1 */}
          <section className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#00FBA9] flex items-center justify-center" style={{ color: "#171717", fontFamily: "'Atkinson Hyperlegible Mono', monospace", fontWeight: 300 }}>
                01
              </div>
              <h2 className="text-2xl m-0" style={{ color: "#171717", fontWeight: 300 }}>
                Connect Your Product Catalogue
              </h2>
            </div>

            <div className=" p-6" style={{ backgroundColor: "#FFFFFF" }}>
              <p className="mb-6" style={{ color: "#171717", fontWeight: 300 }}>
                Start by importing your products into the platform. You have
                several options:
              </p>

              <div className="grid gap-4">
                <div className=" p-5 border" style={{ backgroundColor: "#FAFAFA", borderColor: "#E5E5E5" }}>
                  <h4 className="mb-2 text-lg" style={{ color: "#171717", fontWeight: 300 }}>
                    CSV Import
                  </h4>
                  <p className="text-sm mb-4" style={{ color: "#A9A9A9", fontWeight: 300 }}>
                    Upload a CSV file with your product data. We'll
                    automatically detect columns and map them to our schema.
                  </p>
                  <Link to="/products" search={{ import: true }} className="text-sm" style={{ color: "#3941EB", fontWeight: 300 }}>
                    Go to Products → Import CSV
                  </Link>
                </div>

                <div className=" p-5 border" style={{ backgroundColor: "#FAFAFA", borderColor: "#E5E5E5" }}>
                  <h4 className="mb-2 text-lg" style={{ color: "#171717", fontWeight: 300 }}>
                    Shopify Sync
                  </h4>
                  <p className="text-sm mb-4" style={{ color: "#A9A9A9", fontWeight: 300 }}>
                    Connect your Shopify store to automatically sync your
                    product catalogue.
                  </p>
                  <Link to="/products" className="text-sm" style={{ color: "#3941EB", fontWeight: 300 }}>
                    Go to Products → Sync Shopify
                  </Link>
                </div>

                <div className=" p-5 border" style={{ backgroundColor: "#FAFAFA", borderColor: "#E5E5E5" }}>
                  <h4 className="mb-2 text-lg" style={{ color: "#171717", fontWeight: 300 }}>
                    API Integration
                  </h4>
                  <p className="text-sm mb-4" style={{ color: "#A9A9A9", fontWeight: 300 }}>
                    Use our REST API to programmatically add products from your
                    PIM or custom system.
                  </p>
                  <Link to="/docs" className="text-sm" style={{ color: "#3941EB", fontWeight: 300 }}>
                    View API Documentation
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Step 2 */}
          <section className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#00FBA9] flex items-center justify-center" style={{ color: "#171717", fontFamily: "'Atkinson Hyperlegible Mono', monospace", fontWeight: 300 }}>
                02
              </div>
              <h2 className="text-2xl m-0" style={{ color: "#171717", fontWeight: 300 }}>
                Generate API Keys
              </h2>
            </div>

            <div className=" p-6" style={{ backgroundColor: "#FFFFFF" }}>
              <p className="mb-6" style={{ color: "#171717", fontWeight: 300 }}>
                API keys are required to access the Intelligence APIs. Each key
                can have specific scopes to control access.
              </p>

              <div className=" p-5 mb-6 border" style={{ backgroundColor: "#FAFAFA", borderColor: "#E5E5E5" }}>
                <h4 className="mb-4 text-lg" style={{ color: "#171717", fontWeight: 300 }}>
                  Available Scopes
                </h4>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-center gap-3">
                    <code className="px-2 py-1 rounded text-xs uppercase" style={{ backgroundColor: "#171717", color: "#00FBA9", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>vision:read</code>
                    <span style={{ color: "#A9A9A9", fontWeight: 300 }}>Access the Vision API for image analysis</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <code className="px-2 py-1 rounded text-xs uppercase" style={{ backgroundColor: "#171717", color: "#00FBA9", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>value:read</code>
                    <span style={{ color: "#A9A9A9", fontWeight: 300 }}>Access the Value API for price estimation</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <code className="px-2 py-1 rounded text-xs uppercase" style={{ backgroundColor: "#171717", color: "#00FBA9", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>products:read</code>
                    <span style={{ color: "#A9A9A9", fontWeight: 300 }}>Read product data and enrichments</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <code className="px-2 py-1 rounded text-xs uppercase" style={{ backgroundColor: "#171717", color: "#00FBA9", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>products:write</code>
                    <span style={{ color: "#A9A9A9", fontWeight: 300 }}>Create and update products via API</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <code className="px-2 py-1 rounded text-xs uppercase" style={{ backgroundColor: "#171717", color: "#00FBA9", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>*</code>
                    <span style={{ color: "#A9A9A9", fontWeight: 300 }}>Full access to all APIs</span>
                  </li>
                </ul>
              </div>

              <Link to="/api-keys" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm uppercase tracking-wide transition-colors" style={{ backgroundColor: "#00FBA9", color: "#171717", fontWeight: 300 }}>
                Create API Key
              </Link>
            </div>
          </section>

          {/* Step 3 */}
          <section className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#00FBA9] flex items-center justify-center" style={{ color: "#171717", fontFamily: "'Atkinson Hyperlegible Mono', monospace", fontWeight: 300 }}>
                03
              </div>
              <h2 className="text-2xl m-0" style={{ color: "#171717", fontWeight: 300 }}>
                Make Your First API Call
              </h2>
            </div>

            <div className=" p-6" style={{ backgroundColor: "#FFFFFF" }}>
              <p className="mb-6" style={{ color: "#171717", fontWeight: 300 }}>
                Use your API key to call any of our Intelligence APIs. Here's a
                quick example using the Value API:
              </p>

              {/* Dark code block - appropriate for a single prominent section */}
              <div className=" p-5 overflow-x-auto text-sm" style={{ backgroundColor: "#171717" }}>
                <pre style={{ color: "#FFFFFF", fontFamily: "'Atkinson Hyperlegible Mono', monospace", fontWeight: 300 }}>
                  <span style={{ color: "#00FBA9" }}>curl</span> -X POST \{"\n"}
                  {"  "}https://agentic-commerce.pentatonic.com/api/v1/value/estimate \{"\n"}
                  {"  "}-H <span style={{ color: "#FFB788" }}>"Authorization: Bearer YOUR_API_KEY"</span> \{"\n"}
                  {"  "}-H <span style={{ color: "#FFB788" }}>"Content-Type: application/json"</span> \{"\n"}
                  {"  "}-d <span style={{ color: "#FFB788" }}>'{JSON.stringify(getExampleProduct(), null, 2).replace(/\n/g, "\n    ")}'</span>
                </pre>
              </div>

              <div className="mt-4  p-5 border" style={{ backgroundColor: "#FAFAFA", borderColor: "#E5E5E5" }}>
                <h4 className="mb-3 text-sm uppercase tracking-wide" style={{ color: "#A9A9A9", fontFamily: "'Atkinson Hyperlegible Mono', monospace", fontWeight: 300 }}>
                  Example Response
                </h4>
                <pre className="text-sm overflow-x-auto" style={{ color: "#171717", fontFamily: "'Atkinson Hyperlegible Mono', monospace", fontWeight: 300 }}>
                  {JSON.stringify(getExampleResponse(), null, 2)}
                </pre>
              </div>

              <div className="mt-6">
                <Link to="/docs" style={{ color: "#3941EB", fontWeight: 300 }}>
                  View full API documentation →
                </Link>
              </div>
            </div>
          </section>

          {/* What's Next */}
          <section className="mb-12">
            <h2 className="text-2xl mb-6" style={{ color: "#171717", fontWeight: 300 }}>
              What's Next?
            </h2>

            <div className="grid gap-4 md:grid-cols-2">
              <Link to="/docs" className=" p-6 border transition-all hover:border-[#00FBA9] block" style={{ backgroundColor: "#FFFFFF", borderColor: "#E5E5E5" }}>
                <h3 className="mb-2 text-lg" style={{ color: "#171717", fontWeight: 300 }}>API Reference</h3>
                <p className="text-sm" style={{ color: "#A9A9A9", fontWeight: 300 }}>Explore all available APIs with interactive examples</p>
              </Link>

              <Link to="/products" className=" p-6 border transition-all hover:border-[#00FBA9] block" style={{ backgroundColor: "#FFFFFF", borderColor: "#E5E5E5" }}>
                <h3 className="mb-2 text-lg" style={{ color: "#171717", fontWeight: 300 }}>Manage Products</h3>
                <p className="text-sm" style={{ color: "#A9A9A9", fontWeight: 300 }}>View, search, and manage your product catalogue</p>
              </Link>

              {currentTenant.id === 'arcteryx' ? (
                <Link to="/pin" className=" p-6 border transition-all hover:border-[#00FBA9] block" style={{ backgroundColor: "#FFFFFF", borderColor: "#E5E5E5" }}>
                  <h3 className="mb-2 text-lg" style={{ color: "#171717", fontWeight: 300 }}>PIN Dashboard</h3>
                  <p className="text-sm" style={{ color: "#A9A9A9", fontWeight: 300 }}>Monitor trade-in sessions and takeback activity</p>
                </Link>
              ) : currentTenant.id === 'nespresso' ? (
                <Link to="/nespresso-analytics" className=" p-6 border transition-all hover:border-[#00FBA9] block" style={{ backgroundColor: "#FFFFFF", borderColor: "#E5E5E5" }}>
                  <h3 className="mb-2 text-lg" style={{ color: "#171717", fontWeight: 300 }}>Recycling Analytics</h3>
                  <p className="text-sm" style={{ color: "#A9A9A9", fontWeight: 300 }}>Track capsule recycling and environmental impact</p>
                </Link>
              ) : (
                <Link to="/pin" className=" p-6 border transition-all hover:border-[#00FBA9] block" style={{ backgroundColor: "#FFFFFF", borderColor: "#E5E5E5" }}>
                  <h3 className="mb-2 text-lg" style={{ color: "#171717", fontWeight: 300 }}>PIN Dashboard</h3>
                  <p className="text-sm" style={{ color: "#A9A9A9", fontWeight: 300 }}>Monitor in-store kiosk sessions and analytics</p>
                </Link>
              )}

              <a href="mailto:support@pentatonic.com" className=" p-6 border transition-all hover:border-[#00FBA9] block" style={{ backgroundColor: "#FFFFFF", borderColor: "#E5E5E5" }}>
                <h3 className="mb-2 text-lg" style={{ color: "#171717", fontWeight: 300 }}>Get Help</h3>
                <p className="text-sm" style={{ color: "#A9A9A9", fontWeight: 300 }}>Contact our support team for assistance</p>
              </a>
            </div>
          </section>
        </div>
      </div>
  );
}

